import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "toggle", "settingsPanel"]

  connect() {
    this.scrollPositions = new WeakMap()
    this.scrollPositions.set(document.scrollingElement, window.scrollY)
    const chat = document.querySelector(".chat-messages")
    if (chat) this.scrollPositions.set(chat, chat.scrollTop)
    this.scrollIntent = () => { this.intentAt = performance.now() }
    this.scrollHandler = (event) => this.trackScroll(event)
    this.focusHandler = () => this.showHeader()
    this.keyHandler = (event) => {
      if (!["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName) && ["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " "].includes(event.key)) this.scrollIntent()
    }
    document.addEventListener("scroll", this.scrollHandler, true)
    document.addEventListener("wheel", this.scrollIntent, { passive: true })
    document.addEventListener("touchmove", this.scrollIntent, { passive: true })
    document.addEventListener("keydown", this.keyHandler)
    this.element.addEventListener("focusin", this.focusHandler)
    this.sizeObserver = new ResizeObserver(() => this.element.style.setProperty("--navigation-height", `${this.element.getBoundingClientRect().height}px`))
    this.sizeObserver.observe(this.element)
  }

  disconnect() {
    this.close()
    this.sizeObserver?.disconnect()
    document.removeEventListener("scroll", this.scrollHandler, true)
    document.removeEventListener("wheel", this.scrollIntent)
    document.removeEventListener("touchmove", this.scrollIntent)
    document.removeEventListener("keydown", this.keyHandler)
    this.element.removeEventListener("focusin", this.focusHandler)
  }

  showHeader() {
    this.element.classList.remove("is-header-hidden")
    this.ignoreScrollUntil = performance.now() + 250
    this.scrollMotion = 0
  }

  openSettings() {
    if (!this.hasSettingsPanelTarget) return
    this.close()
    document.dispatchEvent(new Event("chat:close-sidebar"))
    this.showHeader()
    this.previousOverflow = document.body.style.overflow
    this.element.classList.add("ai-settings-open")
    this.activePanel = this.settingsPanelTarget
    this.activeToggle = this.toggleTarget
    this.settingsPanelTarget.setAttribute("role", "dialog")
    this.settingsPanelTarget.setAttribute("aria-modal", "true")
    this.settingsPanelTarget.setAttribute("aria-labelledby", "ai-settings-title")
    document.body.style.overflow = "hidden"
    this.focusable()[0]?.focus()
  }

  limits() {
    if (!this.element.classList.contains("ai-settings-open")) this.openSettings()
    const limits = this.activePanel?.querySelector("#chat-limits")
    if (limits) { limits.open = true; limits.scrollIntoView({ block: "start" }) }
  }

  adminSettings() {
    if (!this.element.classList.contains("ai-settings-open")) this.openSettings()
    const settings = this.activePanel?.querySelector("#admin-ai-settings")
    if (settings) { settings.open = true; settings.scrollIntoView({ block: "start" }) }
  }

  rememberModel(event) {
    const field = event.target.querySelector("[name='selected_model_key']")
    if (field) field.value = document.querySelector("#chat-model")?.value || ""
  }

  openChatSidebar() {
    this.close()
    this.showHeader()
    document.dispatchEvent(new Event("chat:toggle-sidebar"))
  }

  trackScroll(event) {
    const source = event.target === document ? document.scrollingElement : event.target
    if (source !== document.scrollingElement && !source.classList?.contains("chat-messages")) return
    const top = source.scrollTop
    const previous = this.scrollPositions.get(source) ?? top
    this.scrollPositions.set(source, top)
    if (!this.intentAt || performance.now() - this.intentAt > 400 || performance.now() < (this.ignoreScrollUntil || 0)) return
    if (this.element.classList.contains("navigation-open") || this.element.classList.contains("ai-settings-open") || document.activeElement?.closest(".site-header")) return
    const delta = top - previous
    this.scrollMotion = Math.sign(this.scrollMotion || 0) === Math.sign(delta) ? this.scrollMotion + delta : delta
    if (top < 24 || this.scrollMotion < -24) this.showHeader()
    else if (top > 48 && this.scrollMotion > 24) {
      this.element.classList.add("is-header-hidden")
      this.ignoreScrollUntil = performance.now() + 250
      this.scrollMotion = 0
    }
  }

  toggle() {
    this.showHeader()
    if (this.element.classList.contains("navigation-open")) return this.close()
    this.close()
    document.dispatchEvent(new Event("chat:close-sidebar"))
    this.previousOverflow = document.body.style.overflow
    this.element.classList.add("navigation-open")
    this.activePanel = this.panelTarget
    this.activeToggle = this.toggleTarget
    this.toggleTarget.setAttribute("aria-expanded", "true")
    this.panelTarget.setAttribute("role", "dialog")
    this.panelTarget.setAttribute("aria-modal", "true")
    this.panelTarget.setAttribute("aria-labelledby", "navigation-title")
    document.body.style.overflow = "hidden"
    this.focusable()[0]?.focus()
  }

  close(event) {
    if (!this.element.classList.contains("navigation-open") && !this.element.classList.contains("ai-settings-open")) return
    this.element.classList.remove("navigation-open", "ai-settings-open")
    this.toggleTarget.setAttribute("aria-expanded", "false")
    this.activePanel?.removeAttribute("role")
    this.activePanel?.removeAttribute("aria-modal")
    this.activePanel?.removeAttribute("aria-labelledby")
    document.body.style.overflow = this.previousOverflow || ""
    if (event?.type === "click" || event?.key === "Escape") this.activeToggle?.focus()
    this.activePanel = null
  }

  follow(event) { if (event.target.closest("a[href]")) this.close() }

  focusable() {
    return Array.from((this.activePanel || this.panelTarget).querySelectorAll("a[href], button:not([disabled]), input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), summary"))
      .filter((element) => element.getClientRects().length)
  }

  trap(event) {
    if (event.key !== "Tab" || (!this.element.classList.contains("navigation-open") && !this.element.classList.contains("ai-settings-open"))) return
    const elements = this.focusable()
    const first = elements[0], last = elements[elements.length - 1]
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
  }
}
